import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

/**
 * VariableCompensationPanel
 * ----------------------------------------------------------------------------
 * Surfaces three directive-specific metrics that generic HR dashboards miss:
 *   (b) mean gap in variable/complementary components
 *   (d) median gap in same
 *   (e) proportion of female vs male workers receiving variable components
 */

const ParticipationBar = ({ female, male }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-teal-500" />
            <span className="text-xs font-medium text-foreground">{t('dashboard.quartile.female')}</span>
          </div>
          <span className="text-xs font-semibold tabular-nums text-foreground">{female}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-teal-500 transition-all duration-500"
            style={{ width: `${female}%` }}
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-slate-400" />
            <span className="text-xs font-medium text-foreground">{t('dashboard.quartile.male')}</span>
          </div>
          <span className="text-xs font-semibold tabular-nums text-foreground">{male}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-slate-400 transition-all duration-500"
            style={{ width: `${male}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const VariableCompensationPanel = ({ metrics }) => {
  const { t } = useTranslation();
  const participationGap = metrics.variableRecipientsMale - metrics.variableRecipientsFemale;
  const breachVariable = metrics.variableMeanGap > 5;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-semibold text-foreground">{t('dashboard.variable.title')}</h3>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
            Art. 9(b)(d)(e)
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('dashboard.variable.subtitle')}
        </p>
      </div>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className={`p-3 rounded-md border ${breachVariable ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/30'}`}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
              {t('dashboard.variable.meanGap')}
            </div>
            <div className="flex items-baseline gap-0.5">
              <span className={`text-2xl font-bold tabular-nums ${breachVariable ? 'text-destructive' : 'text-foreground'}`}>
                {metrics.variableMeanGap}
              </span>
              <span className="text-sm font-semibold text-muted-foreground">%</span>
            </div>
          </div>
          <div className="p-3 rounded-md border border-border bg-muted/30">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
              {t('dashboard.variable.medianGap')}
            </div>
            <div className="flex items-baseline gap-0.5">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {metrics.variableMedianGap}
              </span>
              <span className="text-sm font-semibold text-muted-foreground">%</span>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
              {t('dashboard.variable.participationRate')}
            </h4>
            <span className={`text-[10px] font-semibold ${
              participationGap >= 10 ? 'text-destructive'
              : participationGap >= 5 ? 'text-warning'
              : 'text-muted-foreground'
            }`}>
              {t('dashboard.variable.disparity', { pp: (participationGap > 0 ? '+' : '') + participationGap })}
            </span>
          </div>
          <ParticipationBar
            female={metrics.variableRecipientsFemale}
            male={metrics.variableRecipientsMale}
          />
        </div>

        {breachVariable && (
          <div className="p-3 rounded-md bg-destructive/5 border border-destructive/20">
            <div className="flex gap-2">
              <Icon name="AlertTriangle" size={14} className="text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-destructive">
                  {t('dashboard.variable.breachWarningTitle')}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('dashboard.variable.breachWarningBody')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VariableCompensationPanel;
